/**
 * Обёртка над BX24.js (ТЗ п. 4.3).
 *
 * Здесь только мост к порталу: инициализация, контекст размещения, сведения
 * о пользователе. Работа с делом — в `storage/cases.ts`, вызовы REST —
 * в `storage/rest.ts`.
 *
 * Профиль дела пишется прямо в портал, минуя сервер: его сохранность
 * не должна зависеть от доступности серверной части (ТЗ п. 8.2).
 */

interface Bx24Api {
  init(callback: () => void): void;
  callMethod(
    method: string,
    params: Record<string, unknown>,
    callback: (result: {
      error(): unknown;
      error_description?(): string;
      data(): unknown;
    }) => void,
  ): void;
  placement: {
    info(): { placement: string; options: Record<string, unknown> };
  };
  getAuth(): { access_token: string; domain: string; member_id: string } | false;
  refreshAuth(callback: (auth: { access_token: string } | false) => void): void;
  getDomain(): string;
  resizeWindow(width: number, height: number): void;
  fitWindow(): void;
  reloadWindow(): void;
}

declare global {
  interface Window {
    BX24?: Bx24Api;
  }
}

export interface CaseContext {
  /** Идентификатор сделки */
  caseId: string;
  entityTypeId: number;
  domain: string;
  /** Приложение открыто вне карточки — например, раздел администрирования */
  standalone: boolean;
}

let initialized = false;

export function bx24(): Bx24Api | null {
  return window.BX24 ?? null;
}

/** Инициализация BX24.js. Вне портала (локальная разработка) — тихо пропускается. */
export async function initBx24(): Promise<void> {
  if (initialized) return;
  const api = bx24();
  if (!api) return;
  await new Promise<void>((resolve) => api.init(() => resolve()));
  initialized = true;
}

function call<T>(method: string, params: Record<string, unknown>): Promise<T> {
  const api = bx24();
  if (!api) return Promise.reject(new Error('Приложение открыто вне портала Битрикс24'));

  return new Promise<T>((resolve, reject) => {
    api.callMethod(method, params, (result) => {
      const error = result.error();
      if (error) {
        reject(new Error(result.error_description?.() ?? 'Ошибка обращения к порталу'));
        return;
      }
      resolve(result.data() as T);
    });
  });
}

/**
 * Сделка в методах `crm.item.*` — сущность с номером 2.
 *
 * Дела ведутся прямо в сделках (решение заказчика 09.09.2026), а размещение
 * `CRM_DEAL_DETAIL_TAB` номера сущности не сообщает: он подразумевается.
 */
export const DEAL_ENTITY_TYPE_ID = 2;

/** Контекст дела из параметров размещения вкладки. */
export function readCaseContext(): CaseContext {
  const api = bx24();
  const fallback: CaseContext = {
    caseId: new URLSearchParams(location.search).get('caseId') ?? '0',
    entityTypeId: Number(new URLSearchParams(location.search).get('entityTypeId') ?? 0),
    domain: api?.getDomain() ?? location.hostname,
    standalone: true,
  };

  if (!api) return fallback;

  try {
    const info = api.placement.info();
    const options = info.options as { ID?: string; ENTITY_ID?: string; ENTITY_TYPE_ID?: string };
    const caseId = String(options.ID ?? options.ENTITY_ID ?? fallback.caseId);

    // Вкладка в карточке сделки номер сущности не передаёт — он известен
    const onDeal = String(info.placement ?? '').toUpperCase().includes('CRM_DEAL');
    const entityTypeId =
      Number(options.ENTITY_TYPE_ID ?? 0) || (onDeal ? DEAL_ENTITY_TYPE_ID : fallback.entityTypeId);

    return {
      caseId,
      entityTypeId,
      domain: api.getDomain(),
      standalone: !info.placement || info.placement === 'DEFAULT',
    };
  } catch {
    return fallback;
  }
}

/** Сведения из карточки контакта, пригодные для блока «Должник». */
export interface ContactDetails {
  last: string;
  first: string;
  middle: string;
  birthDate: string | null;
  phone: string;
  email: string;
}

/** Первое значение множественного поля Битрикс24 (телефон, почта). */
function firstValue(field: unknown): string {
  if (!Array.isArray(field)) return '';
  const entry = field[0] as { VALUE?: string } | undefined;
  return entry?.VALUE ?? '';
}

/**
 * Контакт, привязанный к делу — подстановка в блок «Должник» (FR-06).
 *
 * Подставляются только те сведения, которые в контакте действительно есть:
 * ФИО, дата рождения и контакты. Паспорт, ИНН и СНИЛС в карточке контакта
 * не хранятся, их вводит юрист.
 */
export async function linkedContact(item: Record<string, unknown> | null): Promise<ContactDetails | null> {
  const contactId = item?.contactId ?? item?.contactIds ?? item?.CONTACT_ID;
  const id = Array.isArray(contactId) ? contactId[0] : contactId;
  if (!id) return null;

  try {
    const contact = await call<Record<string, unknown>>('crm.contact.get', { id });
    if (!contact) return null;

    const birthday = typeof contact.BIRTHDATE === 'string' ? contact.BIRTHDATE.slice(0, 10) : '';

    return {
      last: String(contact.LAST_NAME ?? ''),
      first: String(contact.NAME ?? ''),
      middle: String(contact.SECOND_NAME ?? ''),
      birthDate: /^\d{4}-\d{2}-\d{2}$/.test(birthday) ? birthday : null,
      phone: firstValue(contact.PHONE),
      email: firstValue(contact.EMAIL),
    };
  } catch {
    // Недоступность контакта не мешает работе: данные вводятся вручную
    return null;
  }
}

/**
 * Обновление ключа сеанса портала.
 *
 * Портал выдаёт ключ примерно на час, а дело заполняется как раз столько
 * или дольше. По истечении сервер отклоняет ВСЁ: справочники, склонение,
 * блокировку дела, резервные копии, сборку пакета — в журнале за две недели
 * набралась тысяча таких отказов.
 *
 * Обновление умеет сам портал, приложению остаётся его попросить. Ошибка
 * здесь не должна ронять запрос: если обновиться не удалось, пусть наружу
 * выйдет исходный отказ, он понятнее.
 */
export async function refreshAuth(): Promise<boolean> {
  const api = bx24();
  if (!api?.refreshAuth) return false;

  return new Promise<boolean>((resolve) => {
    try {
      api.refreshAuth((auth) => resolve(Boolean(auth && auth.access_token)));
    } catch {
      resolve(false);
    }
  });
}

/** Подгонка высоты вкладки под содержимое. */
export function fitWindow(): void {
  bx24()?.fitWindow();
}
